import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"

// Categorical colors in fixed order, checked for color vision deficiencies.
// Separate steps for the light and the dark theme.
const SERIES_COLORS = {
  light: ["#2a78d6", "#eb6834", "#1baf7a", "#eda100"],
  dark: ["#3987e5", "#d95926", "#199e70", "#c98500"]
}

// Line chart of the bot challenge statistics (admin/bot_challenge_stats).
export default class extends Controller {
  static targets = ["canvas"]

  static values = {
    labels: Array,
    datasets: Array
  }

  connect() {
    this.chart = new Chart(this.canvasTarget, {
      type: "line",
      data: {
        labels: this.labelsValue,
        datasets: this.datasetsValue.map((dataset) => ({
          ...dataset,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          pointHitRadius: 8,
          tension: 0
        }))
      },
      options: {
        maintainAspectRatio: false,
        animation: false,
        interaction: {
          mode: "index",
          intersect: false
        },
        plugins: {
          // A single line is named by the title of the chart
          legend: {
            display: this.datasetsValue.length > 1,
            position: "top",
            align: "start",
            labels: {
              usePointStyle: true,
              pointStyle: "line",
              boxWidth: 16
            }
          },
          tooltip: {
            callbacks: {
              label: (context) => `${context.dataset.label}: ${context.parsed.y.toLocaleString("de-DE")}`
            }
          }
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { maxTicksLimit: 6, maxRotation: 0, autoSkipPadding: 16 }
          },
          y: {
            beginAtZero: true,
            ticks: {
              precision: 0,
              callback: (value) => value.toLocaleString("de-DE")
            }
          }
        }
      }
    })

    this.applyTheme()

    // The color mode switcher sets data-bs-theme on <html>
    this.themeObserver = new MutationObserver(() => this.applyTheme())
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-bs-theme"]
    })
  }

  disconnect() {
    this.themeObserver?.disconnect()
    this.chart?.destroy()
  }

  applyTheme() {
    const theme = document.documentElement.getAttribute("data-bs-theme") === "dark" ? "dark" : "light"
    const style = getComputedStyle(document.documentElement)
    const textColor = style.getPropertyValue("--bs-secondary-color").trim()
    const gridColor = style.getPropertyValue("--bs-border-color-translucent").trim()
    const colors = SERIES_COLORS[theme]

    this.chart.data.datasets.forEach((dataset, index) => {
      dataset.borderColor = colors[index % colors.length]
      dataset.backgroundColor = colors[index % colors.length]
    })

    this.chart.options.color = textColor
    for (const scale of Object.values(this.chart.options.scales)) {
      scale.ticks.color = textColor
      scale.grid.color = gridColor
      scale.border = { color: gridColor }
    }

    this.chart.update("none")
  }
}
